/* Presentation only; lookup ordering lives in the narrowly scoped Vue computed property. */
(() => {
  const root = document.getElementById('app');
  if (!root) return;
  const desktop = window.matchMedia('(min-width: 769px)');
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  let panel = null;
  let scroller = null;
  let cue = null;
  let pending = false;
  let needsFit = true;
  let lastResultHeading = null;

  const contentBottom = () => {
    const last = panel?.lastElementChild;
    return last ? last.getBoundingClientRect().bottom - panel.getBoundingClientRect().top + panel.scrollTop : 0;
  };
  const fitTypography = () => {
    panel.style.removeProperty('--fod-type-scale');
    panel.style.removeProperty('--fod-content-inset');
    if (!desktop.matches || panel.clientHeight === 0 || panel.classList.contains('fod-large-group') || panel.classList.contains('fod-intro-copy')) return;
    const fits = () => contentBottom() <= panel.clientHeight - 12 && panel.scrollWidth <= panel.clientWidth;
    if (!fits()) return;
    const applyScale = value => panel.style.setProperty('--fod-type-scale', String(value));
    const maximum = 1.4;
    applyScale(maximum);
    if (fits()) return;
    let low = 1;
    let high = maximum;
    for (let step = 0; step < 7; step += 1) {
      const middle = (low + high) / 2;
      applyScale(middle);
      if (fits()) low = middle;
      else high = middle;
    }
    applyScale(Math.floor(low * 1000) / 1000);
  };
  const balanceText = () => {
    if (!desktop.matches || panel.classList.contains('fod-large-group') || panel.classList.contains('fod-intro-copy') || panel.scrollHeight > panel.clientHeight) return;
    const available = panel.clientHeight - contentBottom() - 12;
    panel.style.setProperty('--fod-content-inset', Math.max(0, Math.floor(available * .44)) + 'px');
  };
  const setScroller = next => {
    if (next === scroller) return;
    if (scroller) {
      scroller.removeEventListener('scroll', schedule);
      scroller.removeAttribute('tabindex');
      scroller.removeAttribute('role');
      scroller.removeAttribute('aria-label');
    }
    scroller = next;
    scroller?.addEventListener('scroll', schedule, {passive: true});
  };
  const configureGroup = () => {
    if (panel.classList.contains('fod-intro-copy')) { setScroller(panel); return; }
    const summary = panel.querySelector('.fod-group-summary');
    const body = panel.querySelector('.fod-group-body');
    const large = panel.classList.contains('fod-large-group');
    const summaryHeight = summary ? summary.offsetHeight + (parseFloat(getComputedStyle(summary).marginBottom) || 0) : 0;
    const header = document.querySelector('.shopify-section-group-header-group:has(#SiteHeader)');
    const navHeight = header?.getBoundingClientRect().height || parseFloat(getComputedStyle(root).getPropertyValue('--fod-nav-offset')) || 66;
    const viewportHeight = window.visualViewport?.height || window.innerHeight;
    panel.style.setProperty('--fod-summary-top', navHeight + 'px');
    panel.style.setProperty('--fod-summary-height', summaryHeight + 'px');
    panel.classList.toggle('fod-mobile-summary-sticky', !desktop.matches && large && viewportHeight - navHeight - summaryHeight >= 240);
    const pin = desktop.matches && large && panel.clientHeight - summaryHeight >= 160;
    panel.classList.toggle('fod-summary-unpinned', large && !pin);
    setScroller(pin ? body : panel);
  };
  const textBottom = () => {
    const walker = document.createTreeWalker(scroller, NodeFilter.SHOW_TEXT);
    const range = document.createRange();
    let node;
    let bottom = -Infinity;
    while ((node = walker.nextNode())) {
      if (!node.textContent.trim()) continue;
      range.selectNodeContents(node);
      for (const rect of range.getClientRects()) {
        if (rect.width && rect.height) bottom = Math.max(bottom, rect.bottom);
      }
    }
    return bottom;
  };
  const measure = () => {
    pending = false;
    if (!panel || !cue) return;
    if (needsFit) {
      needsFit = false;
      fitTypography();
      balanceText();
      configureGroup();
    }
    const bounds = scroller.getBoundingClientRect();
    const bottom = textBottom();
    const visibleBottom = bounds.top + scroller.clientHeight;
    const overflow = desktop.matches && scroller.scrollHeight > scroller.clientHeight + 1 && bottom + scroller.scrollTop > visibleBottom + 1;
    cue.hidden = !overflow || bottom <= visibleBottom + 1;
    if (overflow) {
      scroller.setAttribute('tabindex', '0');
      scroller.setAttribute('role', 'region');
      scroller.setAttribute('aria-label', panel.classList.contains('fod-intro-copy') ? 'About this bottle lookup' : 'Player history');
    } else {
      scroller.removeAttribute('tabindex');
      scroller.removeAttribute('role');
      scroller.removeAttribute('aria-label');
    }
  };
  function schedule() {
    if (!pending) { pending = true; requestAnimationFrame(measure); }
  }
  const scheduleFit = () => { needsFit = true; schedule(); };
  const advance = () => {
    scroller?.scrollBy({top: Math.max(120, scroller.clientHeight * .75), behavior: reducedMotion.matches ? 'auto' : 'smooth'});
  };
  const resize = new ResizeObserver(scheduleFit);
  const connect = () => {
    const heading = root.querySelector('.fod-result-heading');
    if (heading && heading !== lastResultHeading) {
      requestAnimationFrame(() => { if (heading.isConnected) heading.focus({preventScroll: true}); });
    }
    lastResultHeading = heading;
    const next = root.querySelector('.is-intro .fod-intro-copy, .has-results .player-info-inner > div:not(.check-another)');
    if (next !== panel) {
      if (panel) resize.unobserve(panel);
      cue?.removeEventListener('click', advance);
      panel = next;
      setScroller(panel);
      cue = root.querySelector('.is-intro .fod-scroll-cue, .has-results .fod-scroll-cue');
      if (panel) resize.observe(panel);
      cue?.addEventListener('click', advance);
    }
    scheduleFit();
  };
  new MutationObserver(connect).observe(root, {childList: true, subtree: true, characterData: true});
  resize.observe(root);
  root.addEventListener('toggle', scheduleFit, true);
  desktop.addEventListener('change', scheduleFit);
  window.addEventListener('resize', scheduleFit, {passive: true});
  window.visualViewport?.addEventListener('resize', scheduleFit, {passive: true});
  document.fonts?.ready.then(scheduleFit);
  connect();
})();
